import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchIbamaConamaBoard, fetchIbamaJudges } from '@/services/ibama';

/**
 * The plain-language methodology for the judging-individuals analysis.
 *
 * The page is didactic copy (i18n) over two live readings: the CONAMA CER
 * archive totals and the office-attribution tallies. Every number a section
 * quotes comes from the same endpoints the analysis screens consume, so the
 * explanation can never drift from what the screens show - and when the
 * substrates are absent the live lines degrade to the same refusals, while
 * the explanation itself still stands.
 */
export function IbamaJudgesMethodologyPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const judgesQ = useQuery({ queryKey: ['ibama', 'judges'], queryFn: fetchIbamaJudges });
  const conamaQ = useQuery({
    queryKey: ['ibama', 'conama', 'board'],
    queryFn: fetchIbamaConamaBoard,
  });

  const p = (key: string) => (
    <p className="text-sm text-muted-foreground">{t(`ibama:judgesMethodology.${key}`)}</p>
  );

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">
          {t('ibama:judgesMethodology.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('ibama:judgesMethodology.subtitle')}</p>
      </header>

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="what">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:judgesMethodology.sections.what.title')}
        </h2>
        {p('sections.what.p1')}
      </section>

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="why">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:judgesMethodology.sections.why.title')}
        </h2>
        {p('sections.why.p1')}
      </section>

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="conama">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:judgesMethodology.sections.conama.title')}
        </h2>
        {p('sections.conama.p1')}
        {p('sections.conama.p2')}
        {p('sections.conama.p3')}
        {conamaQ.isPending ? (
          <SkeletonLoader rows={1} />
        ) : conamaQ.error ? (
          <ErrorMessage message={conamaQ.error.message} onRetry={() => void conamaQ.refetch()} />
        ) : (
          <p className="text-sm text-muted-foreground" data-live="conama">
            {t('ibama:judgesMethodology.sections.conama.live', {
              sessions: number.format(conamaQ.data.sessions.length),
              items: number.format(conamaQ.data.items),
              judged: number.format(conamaQ.data.judged),
              relatorItems: number.format(
                conamaQ.data.sessions.reduce((acc, s) => acc + s.with_relator, 0),
              ),
              people: number.format(conamaQ.data.people),
            })}
          </p>
        )}
      </section>

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="office">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:judgesMethodology.sections.office.title')}
        </h2>
        {p('sections.office.p1')}
        {p('sections.office.p2')}
        {p('sections.office.p3')}
        {judgesQ.isPending ? (
          <SkeletonLoader rows={1} />
        ) : judgesQ.error ? (
          <ErrorMessage message={judgesQ.error.message} onRetry={() => void judgesQ.refetch()} />
        ) : (
          <Answer answer={judgesQ.data.bench}>
            {(bench) => (
              <p className="text-sm text-muted-foreground" data-live="office">
                {t('ibama:judgesMethodology.sections.office.live', {
                  firstAttributed: number.format(bench.tallies.first_attributed),
                  firstTotal: number.format(bench.tallies.first_instance_total),
                  boardAttributed: number.format(bench.tallies.board_attributed),
                  boardTotal: number.format(bench.tallies.board_total),
                })}
              </p>
            )}
          </Answer>
        )}
      </section>

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="numbers">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:judgesMethodology.sections.numbers.title')}
        </h2>
        {p('sections.numbers.p1')}
        {judgesQ.isPending ? (
          <SkeletonLoader rows={1} />
        ) : judgesQ.error ? null : (
          <Answer answer={judgesQ.data.bench}>
            {(bench) => (
              <p className="text-sm text-muted-foreground" data-live="floors">
                {t('ibama:judgesMethodology.sections.numbers.p2', {
                  thresholdConama: number.format(bench.threshold_conama),
                  thresholdOffice: number.format(bench.threshold_office),
                })}
              </p>
            )}
          </Answer>
        )}
        {p('sections.numbers.p3')}
      </section>

      <section className="space-y-2 rounded-lg border bg-card p-4" data-section="cautions">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ibama:judgesMethodology.sections.cautions.title')}
        </h2>
        {p('sections.cautions.p1')}
        {p('sections.cautions.p2')}
        {p('sections.cautions.p3')}
      </section>

      <nav className="flex flex-wrap gap-4 text-xs">
        <Link to="/ibama/judges" className="text-primary underline-offset-2 hover:underline">
          {t('ibama:judgesMethodology.links.bench')}
        </Link>
        <Link
          to="/ibama/conama/board"
          className="text-primary underline-offset-2 hover:underline"
        >
          {t('ibama:judgesMethodology.links.conama')}
        </Link>
      </nav>
    </div>
  );
}
